import { localeMappings, supportedLocales } from '~/i18n/config'
import type { SupportedLocale } from '~/i18n/config'
import { watchEffect } from 'vue'

export default defineNuxtPlugin(({ vueApp }) => {
  console.log('i18n.client.ts - Plugin loaded');
  
  // 只在客户端执行
  if (typeof window === 'undefined') return;
  
  // 获取 i18n 实例
  const i18n = vueApp.config.globalProperties.$i18n;
  
  // 初始化语言设置
  initLocale();
  
  /**
   * 初始化语言设置
   */
  function initLocale() {
    try {
      // 1. 首先检查 localStorage 中是否有已保存的语言设置
      const savedLocale = localStorage.getItem('locale');
      let shortLocale: SupportedLocale;
      
      if (savedLocale) {
        // console.log('i18n.client.ts - 发现已保存的语言设置:', savedLocale);
        
        // 检查已保存的语言是否是支持的简短格式
        if (supportedLocales.includes(savedLocale as SupportedLocale)) {
          // 已经是支持的简短格式
          shortLocale = savedLocale as SupportedLocale;
        } else if (Object.keys(localeMappings.fullToShort).includes(savedLocale)) {
          // 是完整格式，需要转换为简短格式
          shortLocale = localeMappings.fullToShort[savedLocale as keyof typeof localeMappings.fullToShort] as SupportedLocale;
          // console.log(`i18n.client.ts - 转换完整格式 ${savedLocale} 到简短格式 ${shortLocale}`);
        } else {
          // 不支持的格式，使用浏览器语言
          shortLocale = detectBrowserLocale();
          // console.log(`i18n.client.ts - 不支持的语言格式 ${savedLocale}，使用浏览器语言 ${shortLocale}`);
        }
      } else {
        // 没有保存的语言设置，使用浏览器语言
        shortLocale = detectBrowserLocale();
        // console.log('i18n.client.ts - 没有已保存的语言设置，使用浏览器语言:', shortLocale);
      }
      
      // 使用 changeLanguage 设置语言
      changeLanguage(shortLocale);
    } catch (error) {
      // console.error('i18n.client.ts - 初始化语言设置失败:', error);
      // 出错时使用英语作为默认语言
      changeLanguage('en');
    }
  }
  
  /**
   * 检测浏览器语言并转换为应用支持的简短格式
   */
  function detectBrowserLocale(): SupportedLocale {
    try {
      // 获取浏览器语言
      const browserLocale = navigator.language.toLowerCase();
      // console.log('i18n.client.ts - 检测到浏览器语言:', browserLocale);
      
      // 尝试直接匹配浏览器语言代码
      if (localeMappings.browserToApp[browserLocale as keyof typeof localeMappings.browserToApp]) {
        return localeMappings.browserToApp[browserLocale as keyof typeof localeMappings.browserToApp] as SupportedLocale;
      }
      
      // 尝试匹配主要语言部分
      const primaryLanguage = browserLocale.split('-')[0];
      if (supportedLocales.includes(primaryLanguage as SupportedLocale)) {
        return primaryLanguage as SupportedLocale;
      }
      
      // 如果用户的语言不在支持的列表中，则使用英语作为默认语言
      return 'en';
    } catch (error) {
      // console.error('i18n.client.ts - 检测浏览器语言失败:', error);
      return 'en';
    }
  }
  
  /**
   * 设置应用程序的语言
   */
  function setAppLocale(shortLocale: SupportedLocale) {
    try {
      // console.log(`i18n.client.ts - 设置应用语言: ${shortLocale}`);
      
      if (i18n && typeof i18n.setLocale === 'function') {
        // 使用类型断言解决类型不匹配问题
        // console.log(`i18n.client.ts - 当前语言: ${i18n.locale}, 正在切换到: ${shortLocale}`);
        i18n.setLocale(shortLocale);
        // console.log('i18n.client.ts - 应用语言设置成功');
        
        // 触发语言变更事件
        if (typeof window !== 'undefined') {
          // console.log('i18n.client.ts - 触发语言变更事件');
          window.dispatchEvent(new CustomEvent('locale-changed', { detail: shortLocale }));
        }
      } else {
        console.error('i18n.client.ts - i18n.setLocale 不是一个函数');
      }
    } catch (error) {
      console.error('i18n.client.ts - 设置应用语言失败:', error);
    }
  }
  
  /**
   * 切换语言
   */
  function changeLanguage(locale: SupportedLocale) {
    if (supportedLocales.includes(locale)) {
      setAppLocale(locale);
      localStorage.setItem('locale', locale);
      return true;
    }
    return false;
  }
  
  // 监听系统语言变化
  if (typeof window !== 'undefined') {
    // 监听语言变化事件
    window.addEventListener('languagechange', () => {
      const newLocale = detectBrowserLocale();
      const currentLocale = i18n.locale as string;
      
      if (newLocale !== currentLocale) {
        changeLanguage(newLocale);
      }
    });

    // 监听 i18n 语言变化
    watchEffect(() => {
      const currentLocale = i18n.locale as string;
      const storedLocale = localStorage.getItem('locale');
      
      // 如果当前语言与 localStorage 中的不一致，则更新 localStorage
      if (currentLocale && storedLocale !== currentLocale && supportedLocales.includes(currentLocale as SupportedLocale)) {
        localStorage.setItem('locale', currentLocale);
      }
    });
  }
  
  // 返回全局方法
  return {
    provide: {
      changeLanguage: (locale: string) => {
        if (supportedLocales.includes(locale as SupportedLocale)) {
          // console.log(`i18n.client.ts - 手动切换语言到: ${locale}`);
          return changeLanguage(locale as SupportedLocale);
        } else {
          // console.error(`i18n.client.ts - 不支持的语言: ${locale}`);
          return false;
        }
      }
    }
  }
}); 